import { fetchy } from '@bridebook/web/lib/fetchy';
import { toMillis } from '@bridebook/toolbox/src/time-utils/time-utils';
import { useQuery } from '@tanstack/react-query';
import { ShowroundCalendarSlot } from '@bridebook/web/lib/showrounds/types/types';
import { nanoid } from 'nanoid';

const CALENDAR_SLOTS_QUERY_CACHE_TIME = toMillis(2, 'min');

export const getCalendarAvailabilitySlotsQueryKey = (supplierId: string, calendarId?: string) => [
  'calendar-slots',
  supplierId,
  calendarId,
];

export type CalendarAvailabilityData = {
  slots: ShowroundCalendarSlot[];
  availabilitiesCount: Record<string, number>;
};

// Query options shared with the queries that load slots of several calendars at once
export const getCalendarAvailabilitySlotsQuery = (supplierId: string, calendarId?: string) => ({
  queryKey: getCalendarAvailabilitySlotsQueryKey(supplierId, calendarId),
  queryFn: async (): Promise<CalendarAvailabilityData> => {
    if (!supplierId || !calendarId) {
      return {
        slots: [],
        availabilitiesCount: {},
      };
    }

    const result = await fetchy().authenticated.get(
      '/api/supplier/[id]/calendar/[calendarId]/available-slots',
      { id: supplierId, calendarId },
    );

    const slots = result.timeSlots.map(
      (slot): ShowroundCalendarSlot => ({
        start: slot.start.toString(),
        id: nanoid(),
      }),
    );

    return {
      slots,
      availabilitiesCount: result.availabilitiesCount,
    };
  },
  refetchOnWindowFocus: false,
  cacheTime: CALENDAR_SLOTS_QUERY_CACHE_TIME,
  staleTime: CALENDAR_SLOTS_QUERY_CACHE_TIME,
});

/**
 * Hook to get the availability slots for a calendar using react query
 */
export const useGetCalendarAvailabilitySlotsQuery = (
  supplierId: string,
  calendarId?: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const { data, error, isFetching, isInitialLoading } = useQuery({
    ...getCalendarAvailabilitySlotsQuery(supplierId, calendarId),
    enabled: !!supplierId && !!calendarId && options?.enabled !== false,
  });

  return {
    slots: data?.slots || ([] as ShowroundCalendarSlot[]),
    availabilitiesCount: data?.availabilitiesCount || {},
    isLoading: isFetching,
    isInitialLoading,
    error,
  };
};
